import { isValidPhoneNumber } from 'libphonenumber-js'

import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

export const CALLER_ID_REGEX = /^[a-zA-Z0-9 ]*$/
export const CALLER_ID_ALL_DIGITS = /^\d+$/
export const CAMPAIGN_ID_REGEX = /^[A-Z0-9]{7}$/
export const PREPEND_STRING_REGEX = /^[a-zA-Z0-9]{0,6}$/

export interface EditValidationErrors {
  name?: string
  callerIdentification?: string
  campaignId?: string
  sourceTracking?: string
  e911?: string
}

export interface CreateValidationErrors extends EditValidationErrors {
  number?: string
  type?: string
  status?: string
  voiceProvider?: string
}

export function validateEditFields(params: {
  name: string
  callerIdentification: string
  campaignId: string
  sourceTracking: string
  e911Address: string
  e911Address2: string
  e911City: string
  e911State: string
  e911PostalCode: string
  // E911 is staff-only on edit. Non-staff callers pass `false` explicitly
  // so they aren't blocked from saving by partially-filled existing data
  // they can't see or fix. Default is `true` (historical behavior) so new
  // callers who forget this flag still validate.
  validateE911?: boolean
  // Deployment region (e.g. 'US' | 'GB'), mirroring the backend's
  // get_default_region(): the backend picks the required E911 set from the
  // region it runs in, not the number's country. On GB there is no state, so
  // state is dropped from the required set.
  e911Region?: string
  t?: PhoneNumbersTranslateFn
}): EditValidationErrors {
  const errors: EditValidationErrors = {}

  if (!params.name.trim()) {
    errors.name = text(
      params.t,
      'phoneNumbers.validation.nameRequired',
      'Name is required',
    )
  }

  if (params.callerIdentification) {
    if (params.callerIdentification.length > 15) {
      errors.callerIdentification = text(
        params.t,
        'phoneNumbers.validation.callerIdTooLong',
        'Max 15 characters',
      )
    } else if (!CALLER_ID_REGEX.test(params.callerIdentification)) {
      errors.callerIdentification = text(
        params.t,
        'phoneNumbers.validation.callerIdInvalidChars',
        'Only letters, numbers, and spaces allowed',
      )
    } else if (CALLER_ID_ALL_DIGITS.test(params.callerIdentification)) {
      errors.callerIdentification = text(
        params.t,
        'phoneNumbers.validation.callerIdAllDigits',
        'Cannot be all digits',
      )
    }
  }

  if (params.campaignId) {
    const upper = params.campaignId.toUpperCase()
    if (!CAMPAIGN_ID_REGEX.test(upper)) {
      errors.campaignId = text(
        params.t,
        'phoneNumbers.validation.campaignIdFormat',
        'Must be exactly 7 alphanumeric characters',
      )
    }
  }

  if (
    params.sourceTracking &&
    !PREPEND_STRING_REGEX.test(params.sourceTracking)
  ) {
    errors.sourceTracking = text(
      params.t,
      'phoneNumbers.validation.sourceTrackingFormat',
      '1-6 alphanumeric characters only',
    )
  }

  if (params.validateE911 !== false) {
    const requiresState = params.e911Region !== 'GB'
    const e911Fields = requiresState
      ? [
          params.e911Address,
          params.e911City,
          params.e911State,
          params.e911PostalCode,
        ]
      : [params.e911Address, params.e911City, params.e911PostalCode]
    const filledCount = e911Fields.filter(Boolean).length
    if (filledCount > 0 && filledCount < e911Fields.length) {
      const fallback = requiresState
        ? 'Address, City, State, and Postal Code are all required when any E911 field is filled'
        : 'Address, City, and Postal Code are all required when any E911 field is filled'
      errors.e911 = text(
        params.t,
        'phoneNumbers.validation.e911Incomplete',
        fallback,
      )
    }
  }

  return errors
}

export function validateCreateFields(params: {
  number: string
  name: string
  type: string
  statusId: string
  voiceProviderId: string
  hasProviders: boolean
  hasDialPrefix?: boolean
  callerIdentification: string
  campaignId: string
  sourceTracking: string
  e911Address: string
  e911City: string
  e911State: string
  e911PostalCode: string
  e911Region?: string
  t?: PhoneNumbersTranslateFn
}): CreateValidationErrors {
  const errors: CreateValidationErrors = {}

  const trimmedNumber = params.number.trim()
  if (!trimmedNumber) {
    errors.number = text(
      params.t,
      'phoneNumbers.validation.numberRequired',
      'Phone number is required',
    )
  } else if (!trimmedNumber.startsWith('+')) {
    errors.number = text(
      params.t,
      'phoneNumbers.validation.numberMissingCountryCode',
      'Phone number must include a country code (e.g. +1 for US, +44 for UK)',
    )
  } else if (!isValidPhoneNumber(trimmedNumber)) {
    errors.number = params.hasDialPrefix
      ? text(
          params.t,
          'phoneNumbers.validation.numberInvalidNational',
          'Enter a valid phone number (e.g. (801) 555-1234)',
        )
      : text(
          params.t,
          'phoneNumbers.validation.numberInvalid',
          'Enter a valid phone number (e.g. +1 (801) 555-1234)',
        )
  }

  if (!params.type) {
    errors.type = text(
      params.t,
      'phoneNumbers.validation.typeRequired',
      'Type is required',
    )
  }

  if (!params.statusId) {
    errors.status = text(
      params.t,
      'phoneNumbers.validation.statusRequired',
      'Status is required',
    )
  }

  if (params.hasProviders && !params.voiceProviderId) {
    errors.voiceProvider = text(
      params.t,
      'phoneNumbers.validation.voiceProviderRequired',
      'Voice provider is required',
    )
  }

  const editErrors = validateEditFields({
    name: params.name,
    callerIdentification: params.callerIdentification,
    campaignId: params.campaignId,
    sourceTracking: params.sourceTracking,
    e911Address: params.e911Address,
    e911Address2: '',
    e911City: params.e911City,
    e911State: params.e911State,
    e911PostalCode: params.e911PostalCode,
    e911Region: params.e911Region,
    t: params.t,
  })

  return { ...errors, ...editErrors }
}
